<script setup lang="ts">
import type { Icons } from '@/components/base/BaseIcon.vue'

export interface ActionStatItem {
  label: string
  value: string | number
  percentage?: string
  tooltip?: string
}

defineProps<{
  icon: Icons
  title: string
  stats: { left: ActionStatItem[]; right: ActionStatItem[] }
}>()
</script>

<template>
  <AppStatsCard>
    <template #title>
      <div class="flex items-center gap-3">
        <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/5">
          <BaseIcon :name="icon" class="h-5 w-5 text-primary" />
        </div>
        <span class="text-base font-semibold text-foreground">{{ title }}</span>
      </div>

      <div class="-mx-5 mt-3 border-t border-border" />
    </template>
    <template #content>
      <div class="-mb-3.5 grid grid-cols-1 gap-x-12 md:grid-cols-2">
        <div>
          <div
            v-for="(stat, index) in stats.left"
            :key="stat.label"
            :class="{ 'border-b border-border': index < stats.left.length - 1 }"
            class="flex items-center justify-between py-4.5"
          >
            <div class="flex items-center gap-1">
              <span class="text-sm font-medium text-muted-foreground">{{ stat.label }}</span>
              <Tooltip>
                <TooltipTrigger as-child>
                  <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
                </TooltipTrigger>
                <TooltipContent>{{ stat.tooltip }}</TooltipContent>
              </Tooltip>
            </div>
            <div class="flex items-baseline gap-1.5">
              <span class="text-sm font-bold text-foreground">{{ stat.value }}</span>
              <span v-if="stat.percentage" class="text-sm text-muted-foreground">({{ stat.percentage }})</span>
            </div>
          </div>
        </div>
        <div>
          <div
            v-for="(stat, index) in stats.right"
            :key="stat.label"
            :class="{ 'border-b border-border': index < stats.right.length - 1 }"
            class="flex items-center justify-between py-4.5"
          >
            <div class="flex items-center gap-1">
              <span class="text-sm font-medium text-muted-foreground">{{ stat.label }}</span>
              <Tooltip>
                <TooltipTrigger as-child>
                  <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
                </TooltipTrigger>
                <TooltipContent>{{ stat.tooltip }}</TooltipContent>
              </Tooltip>
            </div>
            <div class="flex items-baseline gap-1.5">
              <span class="text-sm font-bold text-foreground">{{ stat.value }}</span>
              <span v-if="stat.percentage" class="text-sm text-muted-foreground">({{ stat.percentage }})</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AppStatsCard>
</template>
